import {
  consentRequiresInteraction,
  type IncidentalFindingsConsentConfig,
  type NewYorkSampleRetentionConsentConfig,
  type ResearchParticipationContactConsentConfig,
} from '@/config/uiConfig'

/** Subset of active UI config passed into biological-parent consent UI. */
export interface ComparatorParentConsentConfigSlice {
  incidental?: IncidentalFindingsConsentConfig
  ny?: NewYorkSampleRetentionConsentConfig
  research?: ResearchParticipationContactConsentConfig
}

export function comparatorConsentAnyApplicable(configs: ComparatorParentConsentConfigSlice): boolean {
  return Boolean(configs.incidental?.isApplicable || configs.ny?.isApplicable || configs.research?.isApplicable)
}

/**
 * Whether the incidental-findings section should be gated on an explicit
 * provider choice at submit time. Consistent with NY / research: the section is
 * gated ONLY when it is BOTH org-applicable AND authored
 * `requireInteraction: true`. When `requireInteraction` is absent/false an
 * untouched (`null`) incidental selection imposes no completeness requirement.
 */
export function incidentalConsentGated(configs: ComparatorParentConsentConfigSlice): boolean {
  return configs.incidental?.isApplicable === true && consentRequiresInteraction(configs.incidental)
}

/**
 * Resolved submit-gate flags for the secondary consents driven by the
 * `requireInteraction` UI hint. Each flag is `true` only when the section is
 * BOTH org-applicable AND authored `requireInteraction: true` — i.e. the
 * provider must have made an explicit selection (a defaulted / untouched
 * control does not count). Consumers pass these into the completeness checkers.
 */
export interface ConsentInteractionGates {
  incidentalRequireInteraction: boolean
  nyRetentionRequireInteraction: boolean
  researchContactRequireInteraction: boolean
}

export function resolveConsentInteractionGates(configs: ComparatorParentConsentConfigSlice): ConsentInteractionGates {
  return {
    incidentalRequireInteraction: incidentalConsentGated(configs),
    nyRetentionRequireInteraction: configs.ny?.isApplicable === true && consentRequiresInteraction(configs.ny),
    researchContactRequireInteraction:
      configs.research?.isApplicable === true && consentRequiresInteraction(configs.research),
  }
}
